'use client';

import { createFileRoute } from '@tanstack/react-router';
import { FileText, Plus } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCDPResponses } from '@/features/ghg/api/use-cdp-questionnaire';
import { CDPQuestionnaireDialog } from '@/features/ghg/components/CDPQuestionnaireDialog';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/ghg/cdp-questionnaire')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.cdpQuestionnaire'),
  }),
  component: CDPQuestionnairePage,
});

function CDPQuestionnairePage() {
  const { t } = useTranslation('ghg');
  const [dialogOpen, setDialogOpen] = useState(false);
  const { responses, isLoading } = useCDPResponses();

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <Skeleton className="h-9 w-56" />
          <Skeleton className="h-5 w-96" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('cdpQuestionnairePage.heading')}</h1>
            <p className="mt-1 text-muted-foreground">
              {t('cdpQuestionnairePage.description')}
            </p>
          </div>
          <Button
            onClick={() => {
              setDialogOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('cdpQuestionnairePage.newResponse')}
          </Button>
        </header>

        {responses.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">{t('cdpQuestionnairePage.noResponses')}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('cdpQuestionnairePage.noResponsesDesc')}
            </p>
            <Button
              className="mt-4"
              onClick={() => {
                setDialogOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('cdpQuestionnairePage.startPreparation')}
            </Button>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('cdpQuestionnairePage.cdpYear')}</TableHead>
                  <TableHead>{t('cdpQuestionnairePage.status')}</TableHead>
                  <TableHead>{t('cdpQuestionnairePage.created')}</TableHead>
                  <TableHead>{t('cdpQuestionnairePage.lastUpdated')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {responses.map((response) => (
                  <TableRow key={response.id}>
                    <TableCell className="font-medium">{response.cdpYear}</TableCell>
                    <TableCell>
                      <Badge variant={response.status === 'submitted' ? 'default' : 'secondary'}>
                        {response.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(response.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(response.updatedAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <CDPQuestionnaireDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      </div>
    </div>
  );
}
